'use client';

import { IbanElement } from '@stripe/react-stripe-js';
import { StripeIbanElement } from '@stripe/stripe-js';
import { PaymentMethods } from 'api-graphql-types/enums';
import { Heading } from 'design-system/client';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import styles from '../StripeFields.module.css';
import useResetFieldOnPaymentMethodChange from '../useResetFieldOnPaymentMethodChange';
import { useDonationFormContext, useDonationFormValues } from '@/donationPages/_dependencies/helpers';
import { useIsStripeLoaded } from '@/donationPages/_dependencies/StripeContext';
import { getTranslatorWithFallback } from '@/i18n';
import type { Translator } from '@/i18n/types';
import classNames from 'classnames';
import StripeFieldErrorMessage from '../StripeFieldErrorMessage';
import { getIbanElementProps, invalidStyle, validStyle } from '../stripeInputStyles';
import useStripeEventHandler, { StripeEventHandlerScope } from '../useStripeEventHandler';
function StripeSEPADebitFields() {
  const t_ = useTranslations('nextjs.donate');
  const t = useMemo(() => getTranslatorWithFallback<Translator<'nextjs.donate'>>(t_), [t_]);
  const {
    getFieldState
  } = useDonationFormContext();
  const [paymentMethod, layout] = useDonationFormValues(['payment_method', '_layout']);
  const {
    handleChange,
    handleBlur,
    complete
  } = useStripeEventHandler(StripeEventHandlerScope.SEPA);
  const onReady = useResetFieldOnPaymentMethodChange<StripeIbanElement>(paymentMethod);
  const isStripeLoaded = useIsStripeLoaded();
  const show = paymentMethod === PaymentMethods.StripeSepaDebit;
  if (show && !isStripeLoaded) return <div className={styles.stripeNotLoadedMessage}>
        <Heading level="text-md-important" className={styles.label}>
          {t('sepa_fields.headline')}
        </Heading>
        <p>{t('errors.stripe_sepa_debit_unavailable')}</p>
      </div>;
  if (!isStripeLoaded) return null;
  const stripeCompleteState = getFieldState('_stripe_complete');
  const stripeCompleteIsTouched = stripeCompleteState.isTouched;
  const stripeCompleteHasError = !!stripeCompleteState.error;
  const error = stripeCompleteHasError && stripeCompleteIsTouched;
  const showIframeIncompleteError = layout === 'iframe' && (error || !complete && stripeCompleteIsTouched);
  const stripeCountryState = getFieldState('_stripe_iban_country');
  const countryError = !!stripeCountryState.error;
  const ibanProps = getIbanElementProps({
    complete: countryError ? invalidStyle : validStyle,
    base: countryError ? invalidStyle : validStyle,
    empty: showIframeIncompleteError ? invalidStyle : validStyle
  });

  // because of the new library's behaviour unmounting Stripe components will cause an exception,
  // unless it's removed together with it's provider
  return <div style={{
    display: show ? 'block' : 'none'
  }} className={classNames(styles.stripeField, {
    [styles.emptyError!]: showIframeIncompleteError || countryError
  })}>
      <Heading level="text-md-important" className={styles.label}>
        {t('sepa_fields.headline')}*
      </Heading>
      <IbanElement onReady={onReady} onChange={handleChange} onBlur={handleBlur} {...ibanProps} />
      <StripeFieldErrorMessage className={styles.stripeFieldError} incompleteMessage={t('errors.stripe_sepa_debit_incomplete_iban')} invalidCountryMessage={t('errors.stripe_sepa_debit_non_eea_country_error')} />
    </div>;
}
export default StripeSEPADebitFields;